import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Styles } from "./styles/homeBlog.js";
import { BLOGS_ENDPOINT } from '../config/api';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/600x400?text=Blog';
const SECTION_TITLE = 'Latest From Our Blog';

const formatDisplayDate = (value) => {
    if (!value) {
        return '';
    }
    try {
        const date = new Date(value);
        return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    } catch (err) {
        return '';
    }
};

const HomeBlog = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();

        const loadBlogs = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await fetch(`${BLOGS_ENDPOINT}?limit=4`, { signal: controller.signal });
                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.message || 'Unable to load blogs.');
                }

                const payload = await response.json();
                const items = Array.isArray(payload.blogs) ? payload.blogs : [];
                const sorted = items
                    .filter((blog) => blog.status === 'published')
                    .sort((a, b) => {
                        const left = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
                        const right = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
                        return right - left;
                    });
                setBlogs(sorted.slice(0, 4));
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(err.message || 'Unable to load blogs.');
                    setBlogs([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadBlogs();
        return () => controller.abort();
    }, []);

    const renderContent = () => {
        if (loading) {
            return (
                <Col md="12">
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status" />
                    </div>
                </Col>
            );
        }

        if (error) {
            return (
                <Col md="12">
                    <Alert variant="danger" className="text-center mb-0">
                        {error}
                    </Alert>
                </Col>
            );
        }

        if (blogs.length === 0) {
            return (
                <Col md="12">
                    <Alert variant="light" className="text-center mb-0">
                        No published blogs are available yet. Please check back soon.
                    </Alert>
                </Col>
            );
        }

        return blogs.map((blog) => {
            const authorName = (blog.authorName || '').trim() || 'Admin';
            const commentCount = Array.isArray(blog.comments) ? blog.comments.length : 0;
            const excerpt = blog.excerpt
                ? blog.excerpt.length > 160 ? `${blog.excerpt.slice(0, 157)}...` : blog.excerpt
                : '';
            const coverImage = blog.coverImage || PLACEHOLDER_IMAGE;
            const dateLabel = formatDisplayDate(blog.publishedAt);
            const slugPath = `/blog-details${blog.slug ? `?slug=${encodeURIComponent(blog.slug)}` : ''}`;

            return (
                <Col md="6" key={blog._id || blog.slug}>
                    <div className="blog-post">
                        <Row>
                            <Col lg="6" md="12">
                                <div className="blog-img">
                                    <Link to={process.env.PUBLIC_URL + slugPath}>
                                        <img src={coverImage} alt={blog.title} className="img-fluid" />
                                    </Link>
                                </div>
                            </Col>
                            <Col lg="6" md="12">
                                <div className="blog-content">
                                    <div className="content-box">
                                        <div className="top-content d-flex">
                                            <div className="blog-date text-center">
                                                <p>{dateLabel}</p>
                                            </div>
                                            <div className="blog-title">
                                                <h6>
                                                    <Link to={process.env.PUBLIC_URL + slugPath}>
                                                        {blog.title}
                                                    </Link>
                                                </h6>
                                            </div>
                                        </div>
                                        <div className="blog-desk">
                                            {excerpt && <p>{excerpt}</p>}
                                            <ul className="list-unstyled list-inline">
                                                <li className="list-inline-item">
                                                    <span><i className="las la-user"></i> {authorName}</span>
                                                </li>
                                                <li className="list-inline-item">
                                                    <span><i className="las la-comment"></i> {commentCount}</span>
                                                </li>
                                                {dateLabel && (
                                                    <li className="list-inline-item">
                                                        <span><i className="las la-calendar"></i> {dateLabel}</span>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            );
        });
    };

    return (
        <Styles>
            <section className="home-blog-area">
                <Container>
                    <Row>
                        <Col md="12">
                            <div className="sec-title text-center">
                                <h4>{SECTION_TITLE}</h4>
                            </div>
                        </Col>
                        {renderContent()}
                    </Row>
                </Container>
            </section>
        </Styles>
    );
};

export default HomeBlog;
